import type { OrganizationMember, OrganizationUserRole } from '@/client/types.gen'
import {
  createMemberAppOrganizationOrganizationsMembersPostMutation,
  deleteMemberAppOrganizationOrganizationsMembersUserIdDeleteMutation,
  resendMemberInvitationAppOrganizationOrganizationsMembersUserIdResendInvitationPostMutation,
  updateMemberRoleAppOrganizationOrganizationsMembersUserIdRolePatchMutation,
} from '@/client/@tanstack/react-query.gen'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useSettingsSectionViewed } from '@/hooks/use-analytics'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks/use-auth'
import { parseApiError } from '@/lib/parse-api-error'
import { formatShortDate } from '@/lib/date-utils'
import { formatUserDisplayName } from '@/lib/format-user-display-name'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2, MailIcon, MoreHorizontal, UserCircle, UserPlus, XCircle } from 'lucide-react'
import { useState } from 'react'
import { SettingsPageSkeleton } from './-components/skeleton'

const ROLE_OPTIONS = [
  { value: 'ADMIN' as const, label: 'Admin', description: 'Full access with admin permissions' },
  { value: 'USER' as const, label: 'Member', description: 'Full access with limited permissions' },
  { value: 'VIEWER' as const, label: 'Viewer', description: 'Read-only access' },
]

/** Hook for resending member invitations */
function useResendInvitation() {
  const { toast } = useToast()
  const { refetch } = useAuth()

  return useMutation({
    mutationFn: resendMemberInvitationAppOrganizationOrganizationsMembersUserIdResendInvitationPostMutation().mutationFn,
    onSuccess: () => {
      toast({
        title: 'Invitation resent',
        description: 'The invitation email has been sent successfully.',
        variant: 'success',
      })
      refetch()
    },
    onError: (error) => {
      toast({
        title: 'Failed to resend invitation',
        description: parseApiError(error),
        variant: 'destructive',
      })
    },
  })
}

function InvitePendingIndicator({ memberId }: { memberId: string }) {
  const { mutate: resendInvitation, isPending } = useResendInvitation()

  return (
    <>
      <Badge variant="secondary" className="hidden lg:inline-flex text-orange-600 bg-orange-50 border-orange-200 whitespace-nowrap shrink-0">
        Invite pending
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        className="hidden lg:inline-flex shrink-0 h-8 w-8 p-0"
        onClick={() => resendInvitation({ path: { user_id: memberId } })}
        disabled={isPending}
        title="Resend invitation"
      >
        {isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <MailIcon className="h-4 w-4" />
        )}
      </Button>
    </>
  )
}

function MemberActionsCell({ member, isOrgAdmin, currentUserId }: {
  member: OrganizationMember
  isOrgAdmin: boolean
  currentUserId: string | undefined
}) {
  const { toast } = useToast()
  const { refetch } = useAuth()
  const queryClient = useQueryClient()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const { mutate: resendInvitation, isPending: isResending } = useResendInvitation()

  const { mutate: updateRole, isPending: isUpdatingRole } = useMutation({
    ...updateMemberRoleAppOrganizationOrganizationsMembersUserIdRolePatchMutation(),
    onSuccess: () => {
      toast({ title: 'Role updated', variant: 'success' })
      refetch()
      queryClient.invalidateQueries({ queryKey: ['organizationInfo'] })
    },
    onError: (error) => {
      toast({
        title: 'Failed to update role',
        description: parseApiError(error),
        variant: 'destructive',
      })
    },
  })

  const { mutate: deleteMember, isPending: isDeleting } = useMutation({
    ...deleteMemberAppOrganizationOrganizationsMembersUserIdDeleteMutation(),
    onSuccess: () => {
      toast({
        title: 'Member removed',
        description: 'The user has been removed from the organization.',
        variant: 'success',
      })
      setShowDeleteDialog(false)
      setConfirmText('')
      refetch()
      queryClient.invalidateQueries({ queryKey: ['organizationInfo'] })
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove member',
        description: parseApiError(error),
        variant: 'destructive',
      })
    },
  })

  const isCurrentUser = member.id === currentUserId
  const canEdit = isOrgAdmin && !isCurrentUser
  const isConfirmValid = confirmText === 'REMOVE'

  // Hide menu entirely for non-admins viewing verified members (no actions available)
  const hasAnyActions = canEdit || !member.isVerified
  if (!hasAnyActions) {
    return <></>
  }

  return (
    <>
      <div className="flex items-center justify-end">
        {/* Three-dot menu - always visible, contains mobile info + admin actions */}
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {/* Mobile-only: show member details */}
            <div className="lg:hidden px-2 py-1.5 text-sm">
              <div className="font-medium">{member.email}</div>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={member.role === 'ADMIN' ? 'default' : 'secondary'} className="text-xs">
                  {member.roleLabel}
                </Badge>
                {!member.isVerified && (
                  <Badge variant="secondary" className="text-xs text-orange-600 bg-orange-50 border-orange-200">
                    Invite pending
                  </Badge>
                )}
              </div>
              <div className="text-muted-foreground text-xs mt-1">
                Joined
                {' '}
                {formatShortDate(member.createdAt)}
                {' · '}
                {member.lastLoginAt ? `Last seen ${formatShortDate(member.lastLoginAt, true)}` : 'Never logged in'}
              </div>
            </div>
            <DropdownMenuSeparator className="lg:hidden" />

            {/* Resend invitation - mobile only (desktop has separate button) */}
            {!member.isVerified && (
              <DropdownMenuItem
                onClick={() => {
                  resendInvitation({ path: { user_id: member.id } })
                  setIsOpen(false)
                }}
                disabled={isResending}
              >
                <MailIcon className="h-4 w-4 mr-2" />
                Resend invitation
              </DropdownMenuItem>
            )}

            {/* Admin actions */}
            {canEdit && (
              <>
                {!member.isVerified && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                  }}
                  className="cursor-default font-medium"
                  disabled
                >
                  <UserCircle className="h-4 w-4 mr-2" />
                  Change role
                </DropdownMenuItem>
                {ROLE_OPTIONS.map(option => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => {
                      if (option.value !== member.role) {
                        updateRole({
                          path: { user_id: member.id },
                          body: { role: option.value },
                        })
                      }
                      setIsOpen(false)
                    }}
                    disabled={isUpdatingRole}
                    className="pl-8"
                  >
                    {option.label}
                    {option.value === member.role && ' (current)'}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setShowDeleteDialog(true)
                    setIsOpen(false)
                  }}
                  className="text-red-600 cursor-pointer"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Remove member
                </DropdownMenuItem>
              </>
            )}

          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl">Remove Member</AlertDialogTitle>
            <AlertDialogDescription className="text-base space-y-4 pt-4">
              <p>
                You are about to remove
                {' '}
                <span className="font-semibold text-foreground">{member.email}</span>
                {' '}
                from the team. In order to regain access, a team admin must invite them.
              </p>
              <p className="text-red-500 font-medium">
                Are you sure you want to remove this member?
              </p>
              <div className="space-y-2">
                <p className="text-sm">
                  Type
                  {' '}
                  <span className="font-semibold text-foreground">REMOVE</span>
                  {' '}
                  to confirm.
                </p>
                <Input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder=""
                  className="bg-muted/50"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setConfirmText('')
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!isConfirmValid || isDeleting}
              onClick={(e) => {
                e.preventDefault()
                if (isConfirmValid) {
                  deleteMember({ path: { user_id: member.id } })
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function InviteMemberDialog({ onSuccess }: { onSuccess: () => void }) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<OrganizationUserRole>('USER')

  const resetForm = () => {
    setEmail('')
    setRole('USER')
  }

  const { mutate: createMember, isPending } = useMutation({
    ...createMemberAppOrganizationOrganizationsMembersPostMutation(),
    onSuccess: () => {
      toast({
        title: 'Member invited',
        description: 'An invitation email has been sent.',
        variant: 'success',
      })
      setOpen(false)
      resetForm()
      onSuccess()
    },
    onError: (error) => {
      toast({
        title: 'Failed to invite member',
        description: parseApiError(error),
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMember({
      body: { email, role },
      query: { send_invite: true },
    })
  }

  const isFormValid = email.trim()
  const selectedRole = ROLE_OPTIONS.find(r => r.value === role)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Member
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Invite to your workspace</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={v => setRole(v as OrganizationUserRole)}>
                <SelectTrigger>
                  <SelectValue>
                    {selectedRole && `${selectedRole.label} - ${selectedRole.description}`}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                      {' '}
                      -
                      {option.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isPending || !isFormValid}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function MembersPage() {
  useSettingsSectionViewed('members')
  const { organizationMembers, isLoading, isOrgAdmin, user, organizationInfo, refetch } = useAuth()
  const maxSeats = organizationInfo?.maxSeats ?? undefined
  const queryClient = useQueryClient()

  const handleMemberAdded = () => {
    refetch()
    queryClient.invalidateQueries({ queryKey: ['organizationInfo'] })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  const memberCount = organizationMembers?.length ?? 0
  const isAtSeatLimit = maxSeats !== undefined && memberCount >= maxSeats

  const seatPercentage = maxSeats === undefined ? 0 : (memberCount / maxSeats) * 100
  const isNearLimit = maxSeats !== undefined && memberCount >= maxSeats * 0.8

  return (
    <div className="space-y-4">
      {/* Header with seat usage and invite button */}
      <div className="flex items-center justify-between">
        {maxSeats === undefined ? (
          <div />
        ) : (
          <div className="flex items-center gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {memberCount}
                  {' '}
                  /
                  {' '}
                  {maxSeats}
                  {' '}
                  seats
                </span>
                {isAtSeatLimit && (
                  <Badge variant="secondary" className="text-orange-600 bg-orange-50 border-orange-200">
                    Limit reached
                  </Badge>
                )}
              </div>
              <div
                role="progressbar"
                aria-valuenow={memberCount}
                aria-valuemax={maxSeats}
                aria-label={`${memberCount} of ${maxSeats} seats used`}
                className="w-48 h-2 bg-muted rounded-full overflow-hidden"
              >
                <div
                  className={`h-full rounded-full transition-all ${getProgressBarColor(isAtSeatLimit, isNearLimit)}`}
                  style={{ width: `${Math.min(seatPercentage, 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}
        {isOrgAdmin && !isAtSeatLimit && (
          <InviteMemberDialog onSuccess={handleMemberAdded} />
        )}
        {isOrgAdmin && isAtSeatLimit && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button disabled>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite Member
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>You have reached your seat limit. To add more seats, reach out to support@supplyco.ai or use the chat at the bottom right.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Members table */}
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Email</TableHead>
              <TableHead className="hidden lg:table-cell w-24">Role</TableHead>
              <TableHead className="hidden lg:table-cell w-24">Joined</TableHead>
              <TableHead className="hidden lg:table-cell w-24">Last seen</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(organizationMembers || []).length === 0 ? (
              <TableRow key="empty-row">
                <TableCell colSpan={6} className="text-center py-8">
                  <p className="text-muted-foreground">No members found in this organization.</p>
                </TableCell>
              </TableRow>
            ) : (
              (organizationMembers || []).map(member => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium max-w-50 lg:max-w-none">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarFallback className={member.isVerified ? '' : 'opacity-50'}>
                          {getAvatarInitials(member.firstName, member.lastName, member.email)}
                        </AvatarFallback>
                      </Avatar>
                      <span className={`truncate ${member.isVerified ? '' : 'text-muted-foreground'}`}>
                        {formatUserDisplayName({
                          firstName: member.firstName ?? undefined,
                          lastName: member.lastName ?? undefined,
                          email: member.email ?? undefined,
                        })}
                      </span>
                      {!member.isVerified && <InvitePendingIndicator memberId={member.id} />}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{member.email}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Badge variant={member.role === 'ADMIN' ? 'default' : 'secondary'}>
                      {member.roleLabel}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {formatShortDate(member.createdAt)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {member.lastLoginAt ? formatShortDate(member.lastLoginAt, true) : 'Never'}
                  </TableCell>
                  <TableCell>
                    <MemberActionsCell
                      member={member}
                      isOrgAdmin={isOrgAdmin}
                      currentUserId={user?.id}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function getProgressBarColor(isAtLimit: boolean, isNearLimit: boolean): string {
  if (isAtLimit)
    return 'bg-orange-500'
  if (isNearLimit)
    return 'bg-yellow-500'
  return 'bg-primary'
}

/** Get avatar initials - 2 chars for email-only users, first+last initial otherwise */
function getAvatarInitials(firstName?: string | null, lastName?: string | null, email?: string | null): string {
  if (firstName || lastName) {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`
  }
  // For email-only users, show first 2 chars of email (uppercase)
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return ''
}

export const Route = createFileRoute('/_authed/_user/settings/members')({
  component: MembersPage,
  pendingComponent: SettingsPageSkeleton,
  beforeLoad: () => ({
    getTitle: () => 'Members',
    getDescription: () => 'View and manage your organization members',
  }),
})
